<template>
  <div class="filter-dropdown" ref="root" :class="{ 'is-disabled': disabled, 'is-block': block, 'is-open': isOpen }">
    <!-- Trigger button -->
    <button
        type="button"
        class="filter-trigger"
        :class="{ 'is-active': modelValue !== '' && modelValue != null }"
        @click="toggle"
    >
      <span class="trigger-content">
        <user-avatar
            v-if="selectedOption && showAvatars"
            :src="selectedOption.icon_url"
            :seed="selectedOption.id ?? selectedOption.value"
            :name="selectedOption.label"
            :size="20"
        />
        <span
            v-if="selectedOption && showColors"
            class="color-dot"
            :style="{ background: selectedOption.color }"
        />
        <span class="trigger-label">{{ selectedOption ? selectedOption.label : placeholder }}</span>
      </span>
      <i class="fas fa-chevron-down chevron" :class="{ open: isOpen }" />
    </button>

    <!-- Dropdown panel -->
    <transition name="dropdown-fade">
      <div v-if="isOpen" class="dropdown-panel">
        <!-- Header -->
        <div class="panel-header">{{ label }}</div>

        <!-- Search -->
        <div class="panel-search">
          <i class="fas fa-search search-icon" />
          <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="panel-search-input"
              placeholder="Filtern..."
              @keydown.escape="close"
          />
        </div>

        <!-- Options -->
        <ul class="panel-list">
          <!-- Clear option -->
          <li
              v-if="clearable"
              class="panel-option"
              :class="{ selected: modelValue === '' || modelValue == null }"
              @click="select(null)"
          >
            <span class="option-check">
              <i v-if="!modelValue" class="fas fa-check" />
            </span>
            <span class="option-label muted">{{ placeholder }}</span>
          </li>

          <li v-if="filteredOptions.length === 0" class="panel-empty">Keine Treffer</li>

          <li
              v-for="opt in filteredOptions"
              :key="opt.value"
              class="panel-option"
              :class="{ selected: String(modelValue) === String(opt.value) }"
              @click="select(opt)"
          >
            <span class="option-check">
              <i v-if="String(modelValue) === String(opt.value)" class="fas fa-check" />
            </span>
            <user-avatar
                v-if="showAvatars"
                :src="opt.icon_url"
                :seed="opt.id ?? opt.value"
                :name="opt.label"
                :size="24"
            />
            <span
                v-if="showColors && opt.color"
                class="color-dot"
                :style="{ background: opt.color }"
            />
            <span class="option-label">{{ opt.label }}</span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'

export default {
  name: 'FilterDropdown',
  components: { UserAvatar },

  props: {
    modelValue: { default: '' },
    options:     { type: Array,   required: true },  // [{ value, label, icon_url?, id? }]
    placeholder: { type: String,  default: 'Alle' },
    label:       { type: String,  default: 'Filter' },
    showAvatars: { type: Boolean, default: false },
    showColors:  { type: Boolean, default: false },
    disabled:    { type: Boolean, default: false },
    clearable:   { type: Boolean, default: true },
    block:       { type: Boolean, default: false }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      isOpen:      false,
      searchQuery: ''
    }
  },

  computed: {
    selectedOption() {
      if (!this.modelValue && this.modelValue !== 0) return null
      return this.options.find(o => String(o.value) === String(this.modelValue)) ?? null
    },

    filteredOptions() {
      const q = this.searchQuery.toLowerCase().trim()
      if (!q) return this.options
      return this.options.filter(o => o.label.toLowerCase().includes(q))
    }
  },

  mounted() {
    document.addEventListener('mousedown', this.onOutsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick)
  },

  methods: {
    toggle() {
      if (this.disabled) return
      this.isOpen ? this.close() : this.open()
    },

    open() {
      this.isOpen = true
      this.searchQuery = ''
      this.$nextTick(() => this.$refs.searchInput?.focus())
    },

    close() {
      this.isOpen = false
    },

    select(opt) {
      this.$emit('update:modelValue', opt ? String(opt.value) : '')
      this.close()
    },

    onOutsideClick(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) {
        this.close()
      }
    }
  }
}
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  display: inline-block;
}

.filter-dropdown.is-block {
  display: block;
}

/* Beim Öffnen den ganzen Dropdown (inkl. Panel) über die nachfolgenden
   Karten heben — sonst wird das Panel z.B. in der Auftrags-Bearbeitung von der
   nächsten .info-card verdeckt und wirkt "abgeschnitten". */
.filter-dropdown.is-open {
  z-index: 100;
}

.filter-dropdown.is-block .filter-trigger {
  width: 100%;
  box-sizing: border-box;
  justify-content: space-between;
}

.filter-dropdown.is-disabled .filter-trigger {
  opacity: 0.65;
  cursor: default;
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #4b5563;
}

.filter-dropdown.is-disabled .filter-trigger:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
}

/* Trigger */
.filter-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #24292f;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  white-space: nowrap;
  user-select: none;
}

.filter-trigger:hover {
  background: #f6f8fa;
  border-color: #afb8c1;
}

.filter-trigger.is-active {
  border-color: #0969da;
  background: #ddf4ff;
  color: #0550ae;
}

.trigger-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.trigger-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  font-size: 0.65rem;
  color: #57606a;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.chevron.open {
  transform: rotate(180deg);
}

/* Panel */
.dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 500;
  min-width: 220px;
  max-width: 320px;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(140, 149, 159, 0.2);
  overflow: hidden;
}

.panel-header {
  padding: 8px 14px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #57606a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #eaeef2;
}

.panel-search {
  position: relative;
  padding: 8px 10px;
  border-bottom: 1px solid #eaeef2;
}

.search-icon {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #57606a;
  font-size: 0.78rem;
}

.panel-search-input {
  width: 100%;
  padding: 5px 8px 5px 28px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 0.82rem;
  outline: none;
  background: #f6f8fa;
  transition: border-color 0.15s, background 0.15s;
}

.panel-search-input:focus {
  border-color: #0969da;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.12);
}

.panel-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 280px;
  overflow-y: auto;
}

.panel-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #24292f;
  transition: background 0.1s;
}

.panel-option:hover {
  background: #f6f8fa;
}

.panel-option.selected {
  font-weight: 600;
}

.option-check {
  width: 16px;
  text-align: center;
  flex-shrink: 0;
  color: #0969da;
  font-size: 0.75rem;
}

.color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
  border: 1px solid rgba(0,0,0,0.12);
}

.option-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-label.muted {
  color: #57606a;
  font-weight: 400;
}

.panel-empty {
  padding: 10px 14px;
  font-size: 0.82rem;
  color: #57606a;
  text-align: center;
}

/* Transition */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
